/**
 * Push notifications bridge — Capacitor `@capacitor/push-notifications`.
 *
 * Web behaviour: every helper is a safe no-op via `isNative()`. There IS a
 * browser Push API + Notification API but it requires VAPID keys + a service
 * worker — out of scope for this build. The PWA install path can revisit it.
 *
 * Permission timing: matches the rest of the app — we call
 * `PushNotifications.requestPermissions()` only AFTER the user explicitly
 * opts in (e.g. taps "Enable notifications"), never at app launch.
 */
import axios from 'axios';

import { isNative } from '@/native/permissions';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

let registrationDone = false;
let listenersAttached = false;

/**
 * Request notification permission, then ask FCM/APNs for the device token
 * and POST it to the backend. Safe to call multiple times — only registers
 * once per JS session.
 *
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export async function enablePushNotifications({ onIncoming } = {}) {
  if (!isNative()) return { ok: false, reason: 'not-native' };
  if (registrationDone) return { ok: true, reason: 'already-registered' };

  const { PushNotifications } = await import('@capacitor/push-notifications');
  const { Capacitor } = await import('@capacitor/core');

  // Ask the OS for permission (system dialog only on first call per install).
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') {
    return { ok: false, reason: 'denied' };
  }

  attachListeners(onIncoming);

  // Kick off registration — fires `registration` callback with the FCM token.
  await PushNotifications.register();
  registrationDone = true;

  // Tell the backend who we are. Platform comes from Capacitor.
  // The actual token comes via the `registration` listener — we cache it
  // there and POST asynchronously.
  Capacitor.__unityPlatform = Capacitor.getPlatform();

  return { ok: true };
}

async function attachListeners(onIncoming) {
  if (listenersAttached) return;
  listenersAttached = true;

  const { PushNotifications } = await import('@capacitor/push-notifications');
  const { Capacitor } = await import('@capacitor/core');

  // FCM device token — sent on every registration / token-rotation.
  PushNotifications.addListener('registration', async (event) => {
    const token = event?.value;
    if (!token) return;
    try {
      await axios.post(`${API}/notifications/register-device`, {
        token,
        platform: Capacitor.getPlatform(),  // 'ios' | 'android'
      });
    } catch (e) {
      console.warn('Failed to register device token with backend:', e);
    }
  });

  PushNotifications.addListener('registrationError', (err) => {
    console.warn('PushNotifications registration error:', err);
  });

  // Notification arrived while the app is in the foreground.
  // Background notifications are handled by the OS (the OS shows them).
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    trackBroadcastEvent(notification?.data?.broadcast_id, 'delivered');
    if (typeof onIncoming === 'function') {
      onIncoming({ kind: 'received', notification });
    }
  });

  // User tapped a notification (foreground or background) — payload may
  // include a `data.deep_link` we can navigate to (see deepLinks.js).
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const broadcastId = action?.notification?.data?.broadcast_id;
    const deepLink = action?.notification?.data?.deep_link;
    // 'opened' counts every tap; 'clicked' is only when a deep_link is set
    // (i.e. the tap actually took the user somewhere).
    trackBroadcastEvent(broadcastId, 'opened');
    if (deepLink) trackBroadcastEvent(broadcastId, 'clicked');
    if (typeof onIncoming === 'function') {
      onIncoming({ kind: 'action', action });
    }
  });
}

/**
 * Best-effort POST to /api/notifications/broadcasts/{id}/track.
 * Silently swallows errors — analytics shouldn't ever break the push flow.
 */
async function trackBroadcastEvent(broadcastId, event) {
  if (!broadcastId) return;
  try {
    await axios.post(
      `${API}/notifications/broadcasts/${broadcastId}/track`,
      { event },
      { withCredentials: true }
    );
  } catch (e) {
    console.warn('push analytics track failed:', e?.message || e);
  }
}

/**
 * Tell the backend to forget this device — typically on logout.
 * No-op on web; harmless on native if no token was registered.
 */
export async function disablePushNotifications() {
  if (!isNative()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    // There's no public API to get the token after registration; we rely on
    // the backend to detect unregistered tokens via FCM's UNREGISTERED error.
    // We just stop listening for foreground events here.
    await PushNotifications.removeAllListeners();
    listenersAttached = false;
    registrationDone = false;
  } catch (e) {
    console.warn('disablePushNotifications failed:', e);
  }
}
